/**
 * Phase 16F critical notifications: API contracts (requirements §2.2–2.4, plan §4 J and §14.2).
 *
 * Routes:
 *   GET  /notifications?view=count
 *   GET  /notifications?view=list&tab=new|acknowledged|cleared|resolved
 *   GET  /notifications?id={notificationId}
 *   POST /notifications { action, ids?, notificationId?, task? }
 *   GET  /admin/notification-settings
 *   PUT  /admin/notification-settings
 *
 * Push frame (WebSocket): { type: 'notification.created'|'notification.escalated'|'notification.resolved'|'notification.state',
 * notificationId, newCount }; the client treats every notification.* type as "refresh".
 *
 * The raw shapes the backend sends are the `*Dto` types. `mapNotification` (notification.mapper.ts)
 * turns them into the UI model, so a contract change is absorbed in one place.
 * Notification items carry ids, codes, tiers and counts only (REQ-NTF-08). `patientName` is joined
 * server-side from the patient record, and only when the caller may read that patient's PHI.
 */

/** Trigger codes (requirements §2.3); the catalog is fixed (D9). */
export const NOTIFICATION_CODES = [
  'PA_EXPIRING',
  'PA_EXHAUSTED',
  'PA_DENIED',
  'PA_INITIAL_DUE',
  'UNSTAFFED_SHIFT',
  'MISSED_CLOCK_IN',
  'ABANDONED_VISIT',
  'POC_UNSIGNED',
  'RECERT_OVERDUE',
  'DISCHARGE_SUMMARY_DUE',
  'PATIENT_HOSPITALIZED',
  'PAYMENT_FAILED',
] as const;
export type NotificationCode = (typeof NOTIFICATION_CODES)[number];

/** Email/UI categories (REQ-NTF-07). */
export type NotificationCategory = 'Authorization' | 'Scheduling' | 'Visit' | 'Orders' | 'Discharge' | 'Billing';

/** Shared notification status. */
export type NotificationStatus = 'OPEN' | 'RESOLVED';

/** Per-user recipient state (REQ-NTF-13/14). */
export type NotificationRecipientState = 'NEW' | 'ACKNOWLEDGED' | 'CLEARED';

/** Inbox tabs and filters (REQ-NTF-01, 04, 14). */
export type NotificationTab = 'new' | 'acknowledged' | 'cleared' | 'resolved';

export type NotificationSubjectType = 'PA' | 'SHIFT' | 'VISIT' | 'POC' | 'CERT' | 'DISCHARGE_SUMMARY' | 'PATIENT' | 'SUBSCRIPTION' | string;

/**
 * Where "Open" goes. Either a structured target or an app path:
 *   { section: 'patient', patientId, tab: 'authorizations' }
 *   '/patients/{patientId}/authorizations' · '/schedule/today' · '/admin/organizationAccount' · '/billing/claims'
 */
export interface NotificationDeepLinkObjectDto {
  section?: 'patient' | 'schedule' | 'admin' | 'billing';
  patientId?: string;
  tab?: string | null;
  /** Backend form (catalog.js deepLinkFor): an app path such as /patients/{id}/pa or /schedule/today */
  path?: string | null;
  /** Subject id (PA, shift, summary) when the link points at one record */
  id?: string | null;
}
export type NotificationDeepLinkDto = string | NotificationDeepLinkObjectDto;

/** "Notify the health plan" task on PATIENT_HOSPITALIZED when the primary payer is a Medicaid MCO. */
export type HospitalizationNotifyMethod = 'PHONE' | 'FAX' | 'PORTAL' | 'EMAIL' | 'OTHER';

export interface NotificationTaskDto {
  type: 'NOTIFY_HEALTH_PLAN';
  /** True once someone marked it done (which also resolves the notification for everyone). */
  done: boolean;
  doneAt?: string | null;
  doneBy?: string | null;
  /** YYYY-MM-DD the health plan was notified */
  doneDate?: string | null;
  date?: string | null;
  method?: HospitalizationNotifyMethod | null;
  contact?: string | null;
}

/** One notification as the inbox API returns it (shared fields + my recipient state). */
export interface NotificationDto {
  id?: string;
  notificationId?: string;
  code: NotificationCode | string;
  tier?: string | null;
  category?: NotificationCategory | string | null;
  subjectType?: NotificationSubjectType | null;
  subjectId?: string | null;
  patientId?: string | null;
  /** Only when the caller may read that patient's PHI; never stored on the notification. */
  patientName?: string | null;
  status?: NotificationStatus;
  /** My recipient state. */
  state?: NotificationRecipientState;
  /** Short display text built from codes, counts and dates (no PHI), if the API supplies it. */
  message?: string | null;
  createdAt: string;
  escalatedAt?: string | null;
  resolvedAt?: string | null;
  ackAt?: string | null;
  clearedAt?: string | null;
  deepLink?: NotificationDeepLinkDto | null;
  task?: NotificationTaskDto | null;
  /** "The visit may have started offline" hint for MISSED_CLOCK_IN (plan §14.4). */
  offlineHint?: boolean | null;
  /** Counts/dates for the message (e.g. { daysLeft: 6 }); never PHI. */
  meta?: Record<string, string | number | boolean | null> | null;
}

export interface NotificationCountResponseDto {
  count?: number;
  newCount?: number;
}

export interface NotificationListResponseDto {
  items: NotificationDto[];
}

export type NotificationAction = 'acknowledge' | 'unacknowledge' | 'clear' | 'restore' | 'task' | 'ws-ticket';

export interface NotificationTaskInput {
  date: string;
  method: HospitalizationNotifyMethod;
  contact: string;
}

export interface NotificationActionRequestDto {
  action: NotificationAction;
  ids?: string[];
  notificationId?: string;
  task?: { doneDate: string; method: HospitalizationNotifyMethod; contact: string };
}

export interface NotificationActionResponseDto {
  /** Ids the action applied to. */
  updated?: string[];
  /** Ids skipped (e.g. undo window passed). */
  skipped?: string[];
  newCount?: number;
}

export interface WsTicketResponseDto {
  ticket: string;
  expiresIn?: number;
}

/** WebSocket push frame (REQ-NTF-12a): ids and counts only. */
export interface NotificationPushFrame {
  type: 'notifications.changed';
  notificationId?: string;
  newCount?: number;
}

/** Tenant notification settings (REQ-NTF-06). Critical triggers have no off switch. */
export interface NotificationTriggerSettingDto {
  code: NotificationCode | string;
  roles: string[];
}

export interface NotificationSettingsDto {
  triggers: NotificationTriggerSettingDto[];
  /** 15–60, default 30. */
  missedClockInGraceMinutes: number;
  /** Roles the admin may route to (role keys); optional, the UI falls back to a default list. */
  availableRoles?: Array<string | { key: string; label?: string }>;
  /** Backend name for the same list */
  routableRoles?: Array<string | { key: string; label?: string }>;
  version?: number;
}

export interface UpdateNotificationSettingsRequestDto {
  /** Keyed by trigger code: { PA_EXPIRING: { roles: [...] }, ... } */
  triggers: Record<string, { roles: string[] }>;
  missedClockInGraceMinutes: number;
  version?: number;
}

export const MISSED_CLOCK_IN_GRACE_MIN = 15;
export const MISSED_CLOCK_IN_GRACE_MAX = 60;
export const MISSED_CLOCK_IN_GRACE_DEFAULT = 30;
